import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useThemeStore } from "../stores";
import { useMeetingStore, MediaStates } from "../stores/meetingStore";
import {
	publicMeetingAPI,
	PublicMeetingJoinData,
	JoinMeetingResponse,
	PublicMeeting,
	WeekDay,
} from "../services/api_v1";
import socketService from "../services/socket";
import { liveKitService, PermissionError } from "../services/livekit";
import ParticipantTile from "./ParticipantTile";
import PublicMeetingOnboarding from "./PublicMeetingOnboarding";
import MeetingSettings from "./MeetingSettings";
import LeaveMeetingSheet from "./LeaveMeetingSheet";
import PostMeetingScreen from "./PostMeetingScreen";
import ChatPanel from "./ChatPanel";
import ThumbnailStrip from "./ThumbnailStrip";
import { getResponsiveGridClasses } from "../utils/meetingUtils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar } from "./ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { useToast } from "./ui/toast";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "./ui/tooltip";
import {
	Loader2,
	AlertCircle,
	Video,
	Mic,
	MicOff,
	VideoOff,
	Monitor,
	MonitorOff,
	Users,
	PhoneOff,
	Copy,
	Grid3X3,
	CircleStop,
	Maximize2,
	MessageSquare,
	Minimize2,
	Moon,
	Sun,
	Circle,
	List,
	X,
	Send,
	PowerOff,
	Settings,
	Clock,
} from "lucide-react";
import { Room, Participant, RoomEvent, Track, createLocalVideoTrack, createLocalAudioTrack } from "livekit-client";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";

// Utility function to check if a scheduled meeting is available
interface ScheduleCheckResult {
	isAvailable: boolean;
	reason?: string;
	nextAvailable?: string;
}

const checkScheduledMeetingAvailability = (meeting: PublicMeeting): ScheduleCheckResult => {
	// Instant meetings are always available
	if (meeting.type !== 'scheduled') {
		return { isAvailable: true };
	}

	const now = new Date();
	const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
	const currentTime = now.toTimeString().slice(0, 5); // HH:MM
	const currentDayIndex = now.getDay();
	const dayNames: WeekDay[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
	const currentDay = dayNames[currentDayIndex];

	// Check available days
	if (meeting.available_days && meeting.available_days.length > 0) {
		if (!meeting.available_days.includes(currentDay)) {
			const availableDaysFormatted = meeting.available_days
				.map(d => d.charAt(0).toUpperCase() + d.slice(1))
				.join(', ');
			return {
				isAvailable: false,
				reason: `This meeting is only available on: ${availableDaysFormatted}`,
				nextAvailable: `Next available: ${meeting.available_days[0].charAt(0).toUpperCase() + meeting.available_days[0].slice(1)}`
			};
		}
	}

	// Check date range
	if (meeting.start_date) {
		if (currentDate < meeting.start_date) {
			return {
				isAvailable: false,
				reason: `This meeting is not available yet`,
				nextAvailable: `Available from: ${new Date(meeting.start_date).toLocaleDateString()}`
			};
		}
	}

	if (meeting.end_date) {
		if (currentDate > meeting.end_date) {
			return {
				isAvailable: false,
				reason: `This meeting has ended`,
				nextAvailable: `Meeting ended on: ${new Date(meeting.end_date).toLocaleDateString()}`
			};
		}
	}

	// Check time range (only if we're on a valid date)
	if (meeting.start_time) {
		if (currentTime < meeting.start_time) {
			return {
				isAvailable: false,
				reason: `This meeting has not started yet`,
				nextAvailable: `Starts at: ${meeting.start_time}`
			};
		}
	}

	if (meeting.end_time) {
		if (currentTime > meeting.end_time) {
			return {
				isAvailable: false,
				reason: `This meeting has ended for today`,
				nextAvailable: `Available again tomorrow at: ${meeting.start_time || 'start of day'}`
			};
		}
	}

	return { isAvailable: true };
};

interface PublicChatMessage {
	id: string;
	content: string;
	type: "text" | "system";
	sent_at: string;
	participant: {
		id: string;
		user: {
			id: string;
			first_name: string;
			last_name: string;
		};
	};
}




const PublicMeetingRoom: React.FC = () => {
	const { meetingId, token } = useParams<{
		meetingId: string;
		token: string;
	}>();
	const navigate = useNavigate();
	const darkMode = useThemeStore((state) => state.darkMode);
	const toggleDarkMode = useThemeStore((state) => state.toggleDarkMode);

	// Zustand meetingStore - shared states
	const participantData = useMeetingStore((state) => state.participantData);
	const setParticipantData = useMeetingStore((state) => state.setParticipantData);
	const loading = useMeetingStore((state) => state.loading);
	const setLoading = useMeetingStore((state) => state.setLoading);
	const error = useMeetingStore((state) => state.error);
	const setError = useMeetingStore((state) => state.setError);
	const cameraPermissionGranted = useMeetingStore((state) => state.cameraPermissionGranted);
	const setCameraPermissionGranted = useMeetingStore((state) => state.setCameraPermissionGranted);
	const micPermissionGranted = useMeetingStore((state) => state.micPermissionGranted);
	const setMicPermissionGranted = useMeetingStore((state) => state.setMicPermissionGranted);

	// Local states - specific to the room component
	const [joinResponse, setJoinResponse] = useState<JoinMeetingResponse | null>(
		null,
	);
	const [scheduleCheck, setScheduleCheck] = useState<ScheduleCheckResult | null>(null);
	const [isCheckingAvailability, setIsCheckingAvailability] = useState(true);
	const [room, setRoom] = useState<Room | null>(null);
	const [showOnboarding, setShowOnboarding] = useState(true);
	const [showPostMeeting, setShowPostMeeting] = useState(false);
	const [userMediaStates, setUserMediaStates] = useState<MediaStates | null>(
		null,
	);
	const [participants, setParticipants] = useState<Participant[]>([]);
	const [isConnecting, setIsConnecting] = useState(false);
	const [connectionState, setConnectionState] = useState<
		"disconnected" | "connected" | "connecting"
	>("disconnected");
	const [isCameraOn, setIsCameraOn] = useState(false);
	const [isMicOn, setIsMicOn] = useState(false);
	const [isScreenSharing, setIsScreenSharing] = useState(false);
	const [isScreenShareSupported, setIsScreenShareSupported] = useState(true);
	const [focusedParticipant, setFocusedParticipant] =
		useState<Participant | null>(null);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isFullscreenSupported, setIsFullscreenSupported] = useState(true);

	// Settings sheet state
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [isLeaveSheetOpen, setIsLeaveSheetOpen] = useState(false);
	const [isLandscape, setIsLandscape] = useState(false);

	// Handle screen orientation changes
	useEffect(() => {
		const handleResize = () => {
			setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
		};

		// Initial check
		handleResize();

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Chat-related states
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [unreadMessageCount, setUnreadMessageCount] = useState(0);
	const [messages, setMessages] = useState<PublicChatMessage[]>([]);
	const [typingUsers, setTypingUsers] = useState<string[]>([]);
	const [chatText, setChatText] = useState("");
	const [isChatLoading, setIsChatLoading] = useState(false);
	const [chatError, setChatError] = useState<string>("");

	// Add missing state variables
	const [isRecording, setIsRecording] = useState(false);
	const [recordingId, setRecordingId] = useState<string | null>(null);
	const [recordingStartTime, setRecordingStartTime] = useState<Date | null>(null);
	const [isStartingRecording, setIsStartingRecording] = useState(false);
	const [isStoppingRecording, setIsStoppingRecording] = useState(false);
	const [isEndingMeeting, setIsEndingMeeting] = useState(false);

	// Add refs
	const chatScrollRef = useRef<HTMLDivElement>(null);
	const typingTimeout = useRef<number | null>(null);

	const { addToast } = useToast();

	// Permission checking functions
	const checkMicrophonePermission = useCallback(async () => {
		try {
			if (navigator.permissions) {
				const permission = await navigator.permissions.query({
					name: "microphone" as PermissionName,
				});

				const updatePermissionState = () => {
					if (permission.state === "denied") {
						setMicPermissionGranted(false);
					} else if (permission.state === "granted") {
						setMicPermissionGranted(true);
					} else {
						setMicPermissionGranted(null);
					}
				};

				updatePermissionState();

				// Listen for permission changes
				permission.onchange = updatePermissionState;
			}
		} catch (error) {
			console.error("Error checking microphone permission:", error);
		}
	}, []);

	const checkCameraPermission = useCallback(async () => {
		try {
			if (navigator.permissions) {
				const permission = await navigator.permissions.query({
					name: "camera" as PermissionName,
				});

				const updatePermissionState = () => {
					if (permission.state === "denied") {
						setCameraPermissionGranted(false);
					} else if (permission.state === "granted") {
						setCameraPermissionGranted(true);
					} else {
						setCameraPermissionGranted(null);
					}
				};

				updatePermissionState();

				// Listen for permission changes
				permission.onchange = updatePermissionState;
			}
		} catch (error) {
			console.error("Error checking camera permission:", error);
		}
	}, []);

	const handleJoinFromOnboarding = async (mediaStates: MediaStates) => {
		setUserMediaStates(mediaStates);
		setIsCameraOn(mediaStates.camera_enabled);
		setIsMicOn(mediaStates.microphone_enabled);
		setShowOnboarding(false);
		setLoading(true);
	};

	// Check meeting availability immediately on page load (before onboarding)
	useEffect(() => {
		const checkMeetingAvailability = async () => {
			if (!meetingId || !token) {
				setIsCheckingAvailability(false);
				return;
			}

			try {
				setIsCheckingAvailability(true);
				const response = await publicMeetingAPI.getParticipantByToken(meetingId, token);
				const meeting = response.data.data.meeting;

				// Store participant data for display
				setParticipantData(response.data.data);

				// Check schedule availability
				const scheduleResult = checkScheduledMeetingAvailability(meeting);
				setScheduleCheck(scheduleResult);
			} catch (err: any) {
				console.error("Failed to check meeting availability:", err);
				setError(err.response?.data?.message || "Failed to validate meeting");
			} finally {
				setIsCheckingAvailability(false);
			}
		};

		checkMeetingAvailability();
	}, [meetingId, token]);

	useEffect(() => {
		if (meetingId && token && !showOnboarding) {
			initializeMeeting();
		}

		// Check initial microphone and camera permission status
		checkMicrophonePermission();
		checkCameraPermission();

		// Check if screen sharing is supported on this device
		setIsScreenShareSupported(liveKitService.isScreenShareSupported());

		return () => {
			if (room) {
				liveKitService.disconnect();
			}
		};
	}, [
		meetingId,
		token,
		showOnboarding,
		checkMicrophonePermission,
		checkCameraPermission,
	]);

	// Reset unread count when chat is opened
	useEffect(() => {
		if (isChatOpen) {
			setUnreadMessageCount(0);
		}
	}, [isChatOpen]);

	// Use ref to track chat open state for socket listeners
	const isChatOpenRef = useRef(isChatOpen);
	useEffect(() => {
		isChatOpenRef.current = isChatOpen;
	}, [isChatOpen]);

	// Sync UI state with LiveKit room state periodically
	useEffect(() => {
		if (!room || !room.localParticipant) return;

		const syncMediaStates = () => {
			const actualCameraState = room.localParticipant.isCameraEnabled;
			const actualMicState = room.localParticipant.isMicrophoneEnabled;

			if (actualCameraState !== isCameraOn) {
				console.log(
					`Syncing camera state: UI=${isCameraOn} -> Room=${actualCameraState}`,
				);
				setIsCameraOn(actualCameraState);
			}

			if (actualMicState !== isMicOn) {
				console.log(
					`Syncing mic state: UI=${isMicOn} -> Room=${actualMicState}`,
				);
				setIsMicOn(actualMicState);
			}
		};

		// Sync immediately
		syncMediaStates();

		// Set up periodic sync every 2 seconds
		const syncInterval = setInterval(syncMediaStates, 2000);

		return () => {
			clearInterval(syncInterval);
		};
	}, [room, isCameraOn, isMicOn]);

	const setupSocketListeners = useCallback(() => {
		if (!meetingId) return;

		const handleGroupMessage = (data: any) => {
			console.log(
				"[PublicMeetingRoom] Received message via socket, isChatOpen:",
				isChatOpenRef.current,
				"data:",
				data,
			);

			// Create message object for chat display
			const newMessage: PublicChatMessage = {
				id: data.id || `temp-${Date.now()}`,
				content: data.content,
				type: data.type || "text",
				sent_at: data.sent_at || new Date().toISOString(),
				participant: {
					id: data.participant?.id || "unknown",
					user: {
						id: data.participant?.user?.id || "unknown",
						first_name: data.participant?.user?.first_name || "Unknown",
						last_name: data.participant?.user?.last_name || "",
					},
				},
			};

			// Add message to chat (with duplicate check)
			setMessages((prev) => {
				const messageExists = prev.some((msg) => msg.id === newMessage.id);
				if (messageExists) {
					console.log(
						"[PublicMeetingRoom] Message already exists, skipping:",
						newMessage.id,
					);
					return prev;
				}
				console.log(
					"[PublicMeetingRoom] Adding new message to chat:",
					newMessage.id,
				);
				return [...prev, newMessage];
			});

			// Only increment unread count if chat panel is closed AND message is from someone else
			if (!isChatOpenRef.current) {
				setUnreadMessageCount((prev) => {
					const newCount = prev + 1;
					console.log(
						"[PublicMeetingRoom] Incrementing unread count:",
						prev,
						"->",
						newCount,
					);
					return newCount;
				});
			} else {
				console.log(
					"[PublicMeetingRoom] Chat is open, not incrementing unread count",
				);
			}
		};

		const handleUserTyping = ({
			userId,
			first_name,
			last_name,
			isTyping,
		}: any) => {
			const lastName = last_name === "User" ? "" : last_name;
			const userName = `${first_name} ${lastName}`.trim() || "Unknown User";

			setTypingUsers((prev) => {
				if (isTyping) {
					return prev.includes(userName) ? prev : [...prev, userName];
				} else {
					return prev.filter((user) => user !== userName);
				}
			});
		};

		const handleMeetingEnded = (data: any) => {
			console.log("[PublicMeetingRoom] Meeting ended by host:", data);

			addToast({
				variant: "default",
				title: "Meeting Ended",
				description: data.message || "The meeting has been ended by the host.",
			});

			// Clean up and return to onboarding
			setTimeout(async () => {
				await liveKitService.disconnect();
				setShowOnboarding(false);
				setShowPostMeeting(true);
				setRoom(null);
				setParticipants([]);
				setConnectionState("disconnected");
				setIsRecording(false);
				setRecordingId(null);
				setRecordingStartTime(null);
				setMessages([]);
				setIsChatOpen(false);
				setUnreadMessageCount(0);
			}, 2000); // Give time to show the toast
		};

		// Remove any existing listeners to prevent duplicates
		socketService.off("group-message");
		socketService.off("user-typing");
		socketService.off("meeting-ended");

		// Add our listeners
		socketService.onGroupMessage(handleGroupMessage);
		socketService.onUserTyping(handleUserTyping);
		socketService.getSocket()?.on("meeting-ended", handleMeetingEnded);
		console.log(
			"[PublicMeetingRoom] Socket listeners set up for meeting:",
			meetingId,
		);
	}, [meetingId]);

	// Auto-scroll chat to bottom when messages change
	useEffect(() => {
		if (!chatScrollRef.current) return;
		chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
	}, [messages, isChatOpen]);

	// Fetch chat history when meeting initializes
	const fetchChatHistory = async () => {
		if (!meetingId || !token) return;

		try {
			setIsChatLoading(true);
			setChatError("");

			const response = await publicMeetingAPI.getMessages(meetingId, token);

			if (response.data.success) {
				setMessages(response.data.data.messages || []);
			} else {
				setChatError("Failed to load message history");
			}
		} catch (error: any) {
			console.error("Failed to fetch message history:", error);
			setChatError(error.response?.data?.message || "Failed to load messages");
		} finally {
			setIsChatLoading(false);
		}
	};

	useEffect(() => {
		if (meetingId && token) {
			fetchChatHistory();
		}
	}, [meetingId, token]);

	// Cleanup typing timeout on unmount
	useEffect(() => {
		return () => {
			if (typingTimeout.current) {
				window.clearTimeout(typingTimeout.current);
			}
			if (meetingId) {
				socketService.stopTyping(meetingId);
				// Clean up socket listeners
				socketService.off("group-message");
				socketService.off("user-typing");
				socketService.off("meeting-ended");
			}
		};
	}, [meetingId]);

	// Listen for fullscreen changes (including ESC key)
	useEffect(() => {
		const handleFullscreenChange = () => {
			const isFull =
				document.fullscreenElement ||
				(document as any).webkitFullscreenElement ||
				(document as any).mozFullScreenElement ||
				(document as any).msFullscreenElement;
			setIsFullscreen(!!isFull);
		};

		// Check for fullscreen support
		const elem = document.documentElement;
		const isSupported = !!(
			document.fullscreenEnabled ||
			(document as any).webkitFullscreenEnabled ||
			(document as any).mozFullScreenEnabled ||
			(document as any).msFullscreenEnabled ||
			elem.requestFullscreen ||
			(elem as any).webkitRequestFullscreen ||
			(elem as any).mozRequestFullScreen ||
			(elem as any).msRequestFullscreen
		);
		setIsFullscreenSupported(isSupported);

		document.addEventListener("fullscreenchange", handleFullscreenChange);
		document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
		document.addEventListener("mozfullscreenchange", handleFullscreenChange);
		document.addEventListener("MSFullscreenChange", handleFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", handleFullscreenChange);
			document.removeEventListener(
				"webkitfullscreenchange",
				handleFullscreenChange,
			);
			document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
			document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
		};
	}, []);

	const initializeMeeting = async () => {
		try {
			setLoading(true);
			setError("");

			// participantData is already set from the initial availability check
			// Connect and authenticate Socket.IO with participant data
			const participant = participantData!.participant;

			// First connect to Socket.IO if not already connected
			if (!socketService.getSocket()?.connected) {
				console.log("[PublicMeetingRoom] Connecting to Socket.IO...");
				socketService.connect(token!);

				// Wait for connection before proceeding
				await new Promise((resolve) => {
					const checkConnection = () => {
						if (socketService.getSocket()?.connected) {
							resolve(undefined);
						} else {
							setTimeout(checkConnection, 100);
						}
					};
					checkConnection();
				});
			}

			const socket = socketService.getSocket();
			if (socket?.connected) {
				// Authenticate socket with participant information
				socketService.authenticate(
					participant.user_id,
					participant.user_name.split(" ")[0] || "Unknown",
					participant.user_name.split(" ").slice(1).join(" ") || "",
				);
				// Join meeting room
				socketService.joinMeeting(meetingId!);
				// Setup socket listeners after connection and authentication
				setupSocketListeners();
				console.log(
					"[PublicMeetingRoom] Socket authenticated and joined meeting:",
					meetingId,
				);
			} else {
				console.warn(
					"[PublicMeetingRoom] Socket not connected after connection attempt",
				);
			}

			await connectToLiveKit();
		} catch (error: any) {
			console.error("Failed to initialize public meeting:", error);
			setError(error.response?.data?.message || "Failed to join meeting");
		} finally {
			setLoading(false);
		}
	};

	const connectToLiveKit = async () => {
		try {
			setIsConnecting(true);

			// Join meeting via public API to get LiveKit token
			const joinResponse = await publicMeetingAPI.joinMeeting(
				meetingId!,
				token!,
				{
					camera_enabled: isCameraOn,
					microphone_enabled: isMicOn,
				},
			);

			setJoinResponse(joinResponse.data.data);

			// Get video settings from store
			const videoQuality = useMeetingStore.getState().videoQuality;
			const facingMode = useMeetingStore.getState().facingMode;

			const connectedRoom = await liveKitService.connectToRoom(
				joinResponse.data.data.meeting_id,
				joinResponse.data.data.participant_identity,
				joinResponse.data.data.token,
				videoQuality,
				facingMode,
			);
			setRoom(connectedRoom);

			// Set up room event listeners
			connectedRoom.on(RoomEvent.Connected, () =>
				setConnectionState("connected"),
			);
			connectedRoom.on(RoomEvent.Disconnected, () =>
				setConnectionState("disconnected"),
			);
			connectedRoom.on(
				RoomEvent.ParticipantConnected,
				(participant: Participant) => {
					setParticipants((prev) => [...prev, participant]);
				},
			);
			connectedRoom.on(
				RoomEvent.ParticipantDisconnected,
				(participant: Participant) => {
					setParticipants((prev) =>
						prev.filter((p) => p.identity !== participant.identity),
					);
				},
			);

			// Listen for track events that affect video display
			connectedRoom.on(
				RoomEvent.TrackSubscribed,
				(track, publication, participant) => {
					console.log(
						"Remote track subscribed:",
						track.kind,
						"from",
						participant.identity,
					);
					updateParticipants(connectedRoom);
				},
			);

			connectedRoom.on(
				RoomEvent.TrackUnsubscribed,
				(track, publication, participant) => {
					console.log(
						"Remote track unsubscribed:",
						track.kind,
						"from",
						participant.identity,
					);
					updateParticipants(connectedRoom);
				},
			);

			connectedRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
				console.log(
					"Track published:",
					publication.kind,
					"from",
					participant.identity,
				);
				updateParticipants(connectedRoom);
			});

			connectedRoom.on(
				RoomEvent.TrackUnpublished,
				(publication, participant) => {
					console.log(
						"Track unpublished:",
						publication.kind,
						"from",
						participant.identity,
					);
					updateParticipants(connectedRoom);
				},
			);

			// Listen for local track unpublished at room level
			// This is more reliable for detecting when user stops screen sharing via Chrome's button
			connectedRoom.on(
				RoomEvent.LocalTrackUnpublished,
				(publication) => {
					console.log("LocalTrackUnpublished event:", {
						kind: publication.kind,
						source: publication.source,
						trackName: publication.trackName,
					});

					// Update screen sharing state when screen share track is unpublished
					if (publication.source === Track.Source.ScreenShare) {
						console.log("Screen share stopped via LocalTrackUnpublished, setting isScreenSharing to false");
						setIsScreenSharing(false);
					}

					updateParticipants(connectedRoom);
				},
			);

			// Listen for active speaker changes
			connectedRoom.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
				console.log('Active speakers changed:', speakers.map(s => s.identity));
				// Force re-render of participants to update speaking indicators
				updateParticipants(connectedRoom);
			});

			// Listen for local track published/unpublished events
			connectedRoom.localParticipant.on("trackPublished", (publication) => {
				console.log("Local track published:", {
					kind: publication.kind,
					source: publication.source,
					trackName: publication.trackName,
				});
				updateParticipants(connectedRoom);

				// Update screen sharing state when screen share track is published
				if (publication.source === Track.Source.ScreenShare) {
					console.log("Screen share track published, setting isScreenSharing to true");
					setIsScreenSharing(true);
				}
			});

			connectedRoom.localParticipant.on("trackUnpublished", (publication) => {
				console.log("Local track unpublished:", {
					kind: publication.kind,
					source: publication.source,
					trackName: publication.trackName,
				});

				// Update screen sharing state when screen share track is unpublished
				// This handles the case when user clicks "Stop sharing" from Chrome's native button
				if (publication.source === Track.Source.ScreenShare) {
					console.log("Screen share track unpublished, setting isScreenSharing to false");
					setIsScreenSharing(false);
				}

				updateParticipants(connectedRoom);
			});

			// Update participants list
			const allParticipants = Array.from(
				connectedRoom.remoteParticipants.values(),
			);
			setParticipants(allParticipants);

			// Apply initial media states after room connection
			await applyInitialMediaStates(connectedRoom);

			// Sync UI state with actual room state after initialization
			setTimeout(() => {
				const actualCameraState =
					connectedRoom.localParticipant.isCameraEnabled;
				const actualMicState =
					connectedRoom.localParticipant.isMicrophoneEnabled;

				console.log(
					`Post-connection sync: Camera UI=${isCameraOn} Room=${actualCameraState}, Mic UI=${isMicOn} Room=${actualMicState}`,
				);

				setIsCameraOn(actualCameraState);
				setIsMicOn(actualMicState);
			}, 1000);
		} catch (error: any) {
			console.error("Failed to connect to LiveKit:", error);

			// Handle permission-specific errors
			if (error instanceof PermissionError) {
				setError(error.message);
				addToast({
					title: "Media Permissions Required",
					description: "Please grant camera or microphone access to join the meeting.",
					variant: "destructive",
				});
			} else {
				setError("Failed to connect to video room.");
				addToast({
					title: "Connection Failed",
					description: "Unable to connect to the meeting room. Please try again.",
					variant: "destructive",
				});
			}
		} finally {
			setIsConnecting(false);
		}
	};

	// Focus view handlers
	const handleFocusParticipant = (participant: Participant) => {
		setFocusedParticipant(participant);
		// addToast({
		// 	title: "Participant focused",
		// 	description: `${participant.name || participant.identity} is now in focus view`,
		// 	variant: "success",
		// });
	};

	const handleUnfocusParticipant = () => {
		setFocusedParticipant(null);
		// addToast({
		// 	title: "Focus view disabled",
		// 	description: "Back to grid view",
		// 	variant: "success",
		// });
	};

	const leaveMeeting = async () => {
		try {
			await liveKitService.disconnect();
			socketService.leaveMeeting(meetingId!);
			// Show post meeting screen
			setShowOnboarding(false);
			setShowPostMeeting(true);
			setRoom(null);
			setParticipants([]);
			setConnectionState("disconnected");
			setIsRecording(false);
			setRecordingId(null);
			setRecordingStartTime(null);
			setMessages([]);
			setIsChatOpen(false);
			setUnreadMessageCount(0);
		} catch (error) {
			console.error("Failed to leave meeting:", error);
			// Still show post meeting even if there's an error
			setShowOnboarding(false);
			setShowPostMeeting(true);
			setRoom(null);
			setParticipants([]);
			setConnectionState("disconnected");
		}
	};

	const handleMicrophoneControl = async () => {
		try {
			// If permission not granted, request it only if we are turning it on
			if (!isMicOn && micPermissionGranted !== true) {
				try {
					const audioTrack = await createLocalAudioTrack({ deviceId: undefined });
					// Stop the track immediately after getting permission
					audioTrack.stop();
					setMicPermissionGranted(true);
					// Proceed to toggle
					// Proceed to toggle
				} catch (permError) {
					console.error("Microphone permission denied:", permError);
					addToast({
						title: "Microphone permission denied",
						description:
							"Please allow microphone access in your browser settings",
						variant: "destructive",
					});
					return;
				}
			}

			if (!room) return;

			// Toggle microphone and get the actual result state from LiveKit
			const newState = await liveKitService.toggleMicrophone();

			// Update UI state immediately based on LiveKit's actual state
			const actualState = room.localParticipant.isMicrophoneEnabled;
			setIsMicOn(actualState);

			console.log(
				`Microphone toggled: expected=${newState}, actual=${actualState}`,
			);

			// Notify other participants via Socket.IO
			if (meetingId) {
				socketService.toggleAudio(meetingId, actualState);
			}

			await publicMeetingAPI.updateMediaState(meetingId!, token!, {
				microphone_enabled: actualState,
			});

			// Force re-render of participant tiles
			updateParticipants(room);


		} catch (error) {
			console.error("Failed to control microphone:", error);
		}
	};

	const handleCameraControl = async () => {
		try {
			// If permission not granted, request it only if we are turning it on
			if (!isCameraOn && cameraPermissionGranted !== true) {
				try {
					const videoTrack = await createLocalVideoTrack({ deviceId: undefined });
					// Stop the track immediately after getting permission
					videoTrack.stop();
					setCameraPermissionGranted(true);
					// Proceed to toggle
					// Proceed to toggle
				} catch (permError) {
					console.error("Camera permission denied:", permError);
					addToast({
						title: "Camera permission denied",
						description: "Please allow camera access in your browser settings",
						variant: "destructive",
					});
					return;
				}
			}

			if (!room) return;

			// Toggle camera and get the actual result state from LiveKit
			const newState = await liveKitService.toggleCamera();

			// Update UI state immediately based on LiveKit's actual state
			const actualState = room.localParticipant.isCameraEnabled;
			setIsCameraOn(actualState);

			console.log(
				`Camera toggled: expected=${newState}, actual=${actualState}`,
			);

			// Notify other participants via Socket.IO
			if (meetingId) {
				socketService.toggleVideo(meetingId, actualState);
			}

			await publicMeetingAPI.updateMediaState(meetingId!, token!, {
				camera_enabled: actualState,
			});

			// Force re-render of participant tiles
			updateParticipants(room);


		} catch (error) {
			console.error("Failed to control camera:", error);
		}
	};

	// Helper functions for button states
	const getMicrophoneButtonState = () => {
		// Only show warning if permission is explicitly denied AND mic is off
		if (micPermissionGranted === false && !isMicOn) {
			return {
				variant: "destructive" as const,
				icon: MicOff,
				label: "Grant microphone permission",
				showBadge: true,
				badgeContent: "!",
			};
		}

		if (isMicOn) {
			return {
				variant: "ghost" as const,
				icon: Mic,
				label: "Mute microphone",
				showBadge: false,
				badgeContent: "",
			};
		}

		return {
			variant: "destructive" as const,
			icon: MicOff,
			label: "Unmute microphone",
			showBadge: false,
			badgeContent: "",
		};
	};

	const getCameraButtonState = () => {
		// Only show warning if permission is explicitly denied AND camera is off
		if (cameraPermissionGranted === false && !isCameraOn) {
			return {
				variant: "destructive" as const,
				icon: VideoOff,
				label: "Grant camera permission",
				showBadge: true,
				badgeContent: "!",
			};
		}

		if (isCameraOn) {
			return {
				variant: "ghost" as const,
				icon: Video,
				label: "Turn off camera",
				showBadge: false,
				badgeContent: "",
			};
		}

		return {
			variant: "destructive" as const,
			icon: VideoOff,
			label: "Turn on camera",
			showBadge: false,
			badgeContent: "",
		};
	};

	const handleStartRecording = async () => {
		if (!meetingId || !token || isRecording || isStartingRecording) return;

		if (!participantData?.meeting?.is_recording_enabled) {
			addToast({
				variant: "destructive",
				title: "Recording Disabled",
				description: "Recording is not enabled for this meeting.",
			});
			return;
		}

		if (!participantData?.participant?.is_host) {
			addToast({
				variant: "destructive",
				title: "Permission Denied",
				description: "Only the meeting host can start recording.",
			});
			return;
		}

		setIsStartingRecording(true);
		try {
			const response = await publicMeetingAPI.startRecording(meetingId, token, {
				layout: "grid",
				width: 1920,
				height: 1080,
				show_participant_labels: true, // Enable participant labels by default
			});

			if (response.data.success) {
				setIsRecording(true);
				setRecordingId(response.data.data.recording_id);
				setRecordingStartTime(new Date());


			}
		} catch (error: any) {
			console.error("Failed to start recording:", error);
			addToast({
				variant: "destructive",
				title: "Recording Failed",
				description:
					error.response?.data?.message || "Failed to start recording",
			});
		} finally {
			setIsStartingRecording(false);
		}
	};

	const handleStopRecording = async () => {
		if (
			!meetingId ||
			!token ||
			!recordingId ||
			!isRecording ||
			isStoppingRecording
		)
			return;

		setIsStoppingRecording(true);
		try {
			const response = await publicMeetingAPI.stopRecording(
				meetingId,
				token,
				recordingId,
			);

			if (response.data.success) {
				const recordingStatus = response.data.data.status;

				setIsRecording(false);
				setRecordingId(null);
				setRecordingStartTime(null);

				// Provide different feedback based on recording status
				if (recordingStatus === "failed") {
					addToast({
						variant: "destructive",
						title: "Recording Issue",
						description:
							response.data.data.error ||
							"Recording encountered an issue but has been stopped.",
					});
				}
			}
		} catch (error: any) {
			console.error("Failed to stop recording:", error);

			// Even if there was an error stopping the recording, reset the UI state
			// The recording might have already stopped on the server side
			setIsRecording(false);
			setRecordingId(null);
			setRecordingStartTime(null);

			// Provide helpful error message
			const errorMessage =
				error.response?.data?.message || "Failed to stop recording";
			if (
				errorMessage.includes("EGRESS_ABORTED") ||
				errorMessage.includes("already aborted")
			) {
				addToast({
					variant: "destructive",
					title: "Recording Already Stopped",
					description:
						"The recording was already stopped or encountered an issue. The session has been reset.",
				});
			} else {
				addToast({
					variant: "destructive",
					title: "Recording Error",
					description: errorMessage,
				});
			}
		} finally {
			setIsStoppingRecording(false);
		}
	};

	const handleEndMeeting = async () => {
		if (!meetingId || !token || isEndingMeeting) return;

		if (!participantData?.participant?.is_host) {
			addToast({
				variant: "destructive",
				title: "Permission Denied",
				description: "Only the meeting host can end the meeting.",
			});
			return;
		}

		setIsEndingMeeting(true);
		try {
			const response = await publicMeetingAPI.endMeeting(meetingId, token);

			if (response.data.success) {
				addToast({
					variant: "default",
					title: "Meeting Ended",
					description: "The meeting has been ended successfully.",
				});

				// Disconnect from LiveKit and clean up
				await liveKitService.disconnect();
				socketService.leaveMeeting(meetingId);

				// Show post meeting screen
				setShowOnboarding(false);
				setShowPostMeeting(true);
				setRoom(null);
				setParticipants([]);
				setConnectionState("disconnected");
				setIsRecording(false);
				setRecordingId(null);
				setRecordingStartTime(null);
				setMessages([]);
				setIsChatOpen(false);
				setUnreadMessageCount(0);
			}
		} catch (error: any) {
			console.error("Failed to end meeting:", error);
			addToast({
				variant: "destructive",
				title: "End Meeting Failed",
				description: error.response?.data?.message || "Failed to end meeting",
			});
		} finally {
			setIsEndingMeeting(false);
		}
	};

	const toggleFullscreen = async () => {
		try {
			const elem = document.documentElement;
			const isFull =
				document.fullscreenElement ||
				(document as any).webkitFullscreenElement ||
				(document as any).mozFullScreenElement ||
				(document as any).msFullscreenElement;

			if (!isFull) {
				// Try different fullscreen methods for mobile compatibility
				if (elem.requestFullscreen) {
					await elem.requestFullscreen();
				} else if ((elem as any).webkitRequestFullscreen) {
					// Safari/iOS (iPad)
					await (elem as any).webkitRequestFullscreen();
				} else if ((elem as any).mozRequestFullScreen) {
					// Firefox
					await (elem as any).mozRequestFullScreen();
				} else if ((elem as any).msRequestFullscreen) {
					// IE/Edge
					await (elem as any).msRequestFullscreen();
				} else {
					// Fallback for devices that don't support Element Fullscreen (like iPhone)
					throw new Error("Fullscreen API not supported on this device");
				}
			} else {
				// Exit fullscreen with vendor prefixes
				if (document.exitFullscreen) {
					await document.exitFullscreen();
				} else if ((document as any).webkitExitFullscreen) {
					await (document as any).webkitExitFullscreen();
				} else if ((document as any).mozCancelFullScreen) {
					await (document as any).mozCancelFullScreen();
				} else if ((document as any).msExitFullscreen) {
					await (document as any).msExitFullscreen();
				}
			}
		} catch (error) {
			console.error("Fullscreen toggle failed:", error);
			addToast({
				title: "Fullscreen unavailable",
				description: "Fullscreen mode is not supported on this device or browser.",
				variant: "destructive",
			});
		}
	};

	const handleScreenShare = async () => {
		// Check if screen sharing is enabled for this meeting
		if (!participantData?.meeting?.is_screen_sharing_enabled) {
			addToast({
				title: "Screen sharing disabled",
				description: "Screen sharing is not enabled for this meeting.",
				variant: "destructive",
			});
			return;
		}

		try {
			const newState = await liveKitService.toggleScreenShare();
			setIsScreenSharing(newState);

		} catch (error: any) {
			console.error("Failed to toggle screen share:", error);

			// Handle specific errors
			if (
				error.message?.includes("not supported") ||
				error.name === "NotSupportedError" ||
				error.message?.includes("getDisplayMedia")
			) {
				addToast({
					title: "Screen sharing not supported",
					description: "Screen sharing is not available on this device.",
					variant: "destructive",
				});
			} else if (
				error.message?.includes("Permission denied") ||
				error.name === "NotAllowedError"
			) {
				// User cancelled, do nothing
			} else {
				addToast({
					title: "Screen share failed",
					description: "Unable to share screen. Please try again.",
					variant: "destructive",
				});
			}
		}
	};

	const applyInitialMediaStates = async (connectedRoom: Room) => {
		try {
			console.log("Applying initial media states:", {
				camera: isCameraOn,
				mic: isMicOn,
			});

			// Set camera state based on saved preference
			if (isCameraOn !== connectedRoom.localParticipant.isCameraEnabled) {
				console.log(
					`Setting camera to ${isCameraOn ? "enabled" : "disabled"} from saved state`,
				);
				await connectedRoom.localParticipant.setCameraEnabled(isCameraOn);
			}

			// Set microphone state based on saved preference
			if (isMicOn !== connectedRoom.localParticipant.isMicrophoneEnabled) {
				console.log(
					`Setting microphone to ${isMicOn ? "enabled" : "disabled"} from saved state`,
				);
				await connectedRoom.localParticipant.setMicrophoneEnabled(isMicOn);
			}
		} catch (error) {
			console.error("Failed to apply initial media states:", error);
		}
	};

	const updateParticipants = (room: Room) => {
		const allParticipants = Array.from(room.remoteParticipants.values());
		setParticipants(allParticipants);

		// Only update screen sharing state from room, keep camera/mic state managed by user actions
		if (room.localParticipant) {
			setIsScreenSharing(room.localParticipant.isScreenShareEnabled);
		}
	};

	// Show post meeting screen if applicable
	if (showPostMeeting) {
		return <PostMeetingScreen />;
	}

	// Show loading while checking meeting availability
	if (isCheckingAvailability) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<Card className="bg-card border border-border shadow-2xl">
					<CardContent className="p-8">
						<div className="flex flex-col items-center space-y-6">
							<div className="relative">
								<div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
								<img
									src="/logo.svg"
									alt="Logo"
									className="absolute inset-0 m-auto h-8 w-8"
								/>
							</div>
							<div className="text-center space-y-2">
								<h3 className="text-lg font-semibold text-foreground">
									Checking Meeting
								</h3>
								<p className="text-muted-foreground text-sm">
									Validating meeting availability...
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Show schedule unavailable screen BEFORE onboarding for scheduled meetings outside their time window
	if (scheduleCheck && !scheduleCheck.isAvailable) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<Card className="bg-card border border-border shadow-2xl max-w-md mx-4">
					<CardHeader>
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-amber-500/20 rounded-full">
								<Clock className="h-6 w-6 text-amber-500" />
							</div>
							<CardTitle className="text-foreground">
								Meeting Not Available
							</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="space-y-2">
							<h3 className="font-medium text-foreground">
								{participantData?.meeting?.title || "Scheduled Meeting"}
							</h3>
							<p className="text-muted-foreground">{scheduleCheck.reason}</p>
						</div>
						{scheduleCheck.nextAvailable && (
							<div className="p-3 bg-muted/50 rounded-lg border border-border">
								<p className="text-sm text-foreground">
									{scheduleCheck.nextAvailable}
								</p>
							</div>
						)}
						{participantData?.meeting?.type === 'scheduled' && (
							<div className="p-3 bg-muted/30 rounded-lg border border-border space-y-2">
								<h4 className="font-medium text-sm text-foreground">Schedule Details:</h4>
								<ul className="text-xs text-muted-foreground space-y-1">
									{participantData.meeting.start_date && (
										<li>• Start Date: {new Date(participantData.meeting.start_date).toLocaleDateString()}</li>
									)}
									{participantData.meeting.end_date && (
										<li>• End Date: {new Date(participantData.meeting.end_date).toLocaleDateString()}</li>
									)}
									{participantData.meeting.start_time && (
										<li>• Start Time: {participantData.meeting.start_time}</li>
									)}
									{participantData.meeting.end_time && (
										<li>• End Time: {participantData.meeting.end_time}</li>
									)}
									{participantData.meeting.available_days && participantData.meeting.available_days.length > 0 && (
										<li>• Available Days: {participantData.meeting.available_days.map((d: string) => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}</li>
									)}
								</ul>
							</div>
						)}
						<Button
							onClick={() => window.location.reload()}
							className="w-full"
							variant="outline"
						>
							Refresh
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Show onboarding for media selection (only if meeting is available)
	if (showOnboarding && meetingId && token) {
		return (
			<PublicMeetingOnboarding
				meetingId={meetingId}
				token={token}
				onJoinMeeting={handleJoinFromOnboarding}
			/>
		);
	}

	if (loading) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<Card className="bg-card border border-border shadow-2xl">
					<CardContent className="p-8">
						<div className="flex flex-col items-center space-y-6">
							<div className="relative">
								<div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
								<img
									src="/logo.svg"
									alt="Logo"
									className="absolute inset-0 m-auto h-8 w-8"
								/>
							</div>
							<div className="text-center space-y-2">
								<h3 className="text-lg font-semibold text-foreground">
									Joining Meeting
								</h3>
								<p className="text-muted-foreground text-sm">
									Connecting to the video room...
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (error) {
		const isPermissionError = error.includes('camera and microphone permissions are required');

		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<Card className="bg-card border border-border shadow-2xl max-w-md mx-4">
					<CardHeader>
						<div className="flex items-center space-x-3">
							<div className="p-2 bg-destructive/20 rounded-full">
								<AlertCircle className="h-6 w-6 text-destructive" />
							</div>
							<CardTitle className="text-foreground">
								{isPermissionError ? "Media Permissions Required" : "Connection Error"}
							</CardTitle>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-muted-foreground">{error}</p>
						{isPermissionError && (
							<div className="p-3 bg-muted/50 rounded-lg border border-border">
								<h4 className="font-medium text-sm text-foreground mb-2">
									How to grant permissions:
								</h4>
								<ul className="text-xs text-muted-foreground space-y-1">
									<li>• Click the camera/microphone icon in your browser's address bar</li>
									<li>• Select "Allow" for camera and/or microphone access</li>
									<li>• Refresh the page to retry connection</li>
								</ul>
							</div>
						)}
						<Button
							onClick={() => window.location.reload()}
							className="w-full"
							variant="gradient"
						>
							{isPermissionError ? "Grant Permissions & Retry" : "Try Again"}
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<TooltipProvider>
			<div className="fixed inset-0 bg-background text-foreground flex flex-col theme-transition">
				{/* Meeting Header - Hidden in mobile landscape spotlight mode */}
				<div className={cn(
					"flex-shrink-0 px-3 md:px-6 py-2.5 md:py-3 backdrop-blur-sm shadow-sm transition-all duration-300",
					darkMode
						? "bg-gradient-to-b from-black/70 via-black/60 to-transparent"
						: "bg-gradient-to-b from-white/80 via-white/60 to-transparent",
					isLandscape && focusedParticipant ? "hidden" : "block"
				)}>
					<div className="flex items-center justify-between max-w-[2000px] mx-auto">
						<div className="flex items-center space-x-2 md:space-x-3 min-w-0">
							<div className="bg-gradient-to-br from-primary/30 to-primary/10 rounded-lg p-1.5 md:p-2 flex items-center justify-center flex-shrink-0 shadow-sm">
								<img
									src="/logo.svg"
									alt="Logo"
									className="h-8 w-8 md:h-10 md:w-10"
								/>
							</div>
							<div className="space-y-0.5 min-w-0 flex-1">
								<h1 className="text-foreground font-semibold text-sm md:text-base truncate">
									{participantData?.meeting?.title || "Public Meeting"}
								</h1>
								<div className="flex items-center gap-2">
									<Badge
										variant="secondary"
										className="bg-secondary/80 text-secondary-foreground border-border/50 rounded-md text-xs px-2 py-0.5"
									>
										<Users className="h-3 w-3 mr-1" />
										{participants.length + 1} {participants.length === 0 ? 'participant' : 'participants'}
									</Badge>
									{connectionState === 'connected' && (
										<div className="flex items-center gap-1.5">
											<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
											<span className="text-xs text-muted-foreground hidden sm:inline">Connected</span>
										</div>
									)}
								</div>
							</div>
						</div>

						<div className="flex items-center space-x-0.5 md:space-x-1 flex-shrink-0">
							{/* <Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											navigator.clipboard.writeText(window.location.href)
										}
									>
										<Copy className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Copy meeting link</p>
								</TooltipContent>
							</Tooltip> */}


							{isFullscreenSupported && (
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="sm"
											onClick={toggleFullscreen}
											className="text-foreground hover:bg-accent hover:text-accent-foreground"
										>
											{isFullscreen ? (
												<Minimize2 className="h-4 w-4" />
											) : (
												<Maximize2 className="h-4 w-4" />
											)}
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p>{isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}</p>
									</TooltipContent>
								</Tooltip>
							)}

							<Tooltip>
								<TooltipTrigger asChild>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setIsSettingsOpen(true)}
										className="text-foreground hover:bg-accent hover:text-accent-foreground"
									>
										<Settings className="h-4 w-4" />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Meeting settings</p>
								</TooltipContent>
							</Tooltip>

							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="ghost" size="sm" onClick={toggleDarkMode}>
										{darkMode ? (
											<Sun className="h-4 w-4" />
										) : (
											<Moon className="h-4 w-4" />
										)}
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>Toggle {darkMode ? "light" : "dark"} mode</p>
								</TooltipContent>
							</Tooltip>
						</div>
					</div>
				</div>

				{/* Main Video Area - Flexible layout */}
				<div className="flex-1 flex flex-col overflow-hidden">
					{!isConnecting && room && (
						<AnimatePresence mode="wait">
							{focusedParticipant ? (
								// Spotlight Mode - Focused participant with thumbnail strip
								<div
									key="spotlight-mode"
									className={cn(
										"flex-1 flex h-full min-h-0",
										isLandscape ? "flex-row" : "flex-col"
									)}>
									{/* Main spotlight video */}
									<div className="flex-1 p-2 md:p-6 flex flex-col min-h-0">
										<div className="flex-1 w-full max-w-[1800px] mx-auto relative min-h-0">
											<motion.div
												layoutId={focusedParticipant.identity}
												className="h-full bg-gradient-to-br from-muted/20 via-muted/30 to-muted/20 dark:from-muted/30 dark:via-muted/40 dark:to-muted/30 rounded-2xl overflow-hidden shadow-2xl border border-border/50 relative group"
												initial={{ opacity: 0, scale: 0.95 }}
												animate={{ opacity: 1, scale: 1 }}
												exit={{ opacity: 0, scale: 0.95 }}
												transition={{
													type: "spring",
													stiffness: 300,
													damping: 30,
													opacity: { duration: 0.2 },
												}}
											>
												<ParticipantTile
													participant={focusedParticipant}
													isLocal={focusedParticipant === room?.localParticipant}
													isFocused={true}
												/>
												{/* Exit spotlight button */}
												<Tooltip>
													<TooltipTrigger asChild>
														<button
															onClick={handleUnfocusParticipant}
															className="absolute top-3 right-3 md:top-4 md:right-4 p-2 md:p-2 bg-black/80 hover:bg-black/90 border-2 border-white/30 rounded-xl transition-all duration-300 z-10 backdrop-blur-md shadow-xl"
															aria-label="Exit spotlight view"
														>
															<Minimize2 className="h-4 w-4 md:h-5 md:w-5 text-white" />
														</button>
													</TooltipTrigger>
													<TooltipContent>
														<p>Exit spotlight and return to grid</p>
													</TooltipContent>
												</Tooltip>
											</motion.div>
										</div>
									</div>

									{/* Thumbnail strip */}
									<ThumbnailStrip
										participants={participants}
										localParticipant={room.localParticipant}
										activeParticipant={focusedParticipant}
										onThumbnailClick={handleFocusParticipant}
										position={isLandscape ? "right" : "bottom"}
									/>
								</div>
							) : participants.length === 0 && room.localParticipant ? (
								// Solo participant view - centered and elegant
								<motion.div
									key="solo-view"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="flex-1 p-4 md:p-8 flex items-center justify-center overflow-hidden"
								>
									<div className="w-full max-w-5xl relative">
										<div className="aspect-video bg-gradient-to-br from-muted/20 via-muted/30 to-muted/20 dark:from-muted/30 dark:via-muted/40 dark:to-muted/30 rounded-2xl overflow-hidden shadow-2xl border border-border/50 group relative">
											<ParticipantTile
												participant={room.localParticipant}
												isLocal={true}
												onEnlarge={() =>
													handleFocusParticipant(room.localParticipant)
												}
											/>

										</div>
									</div>
								</motion.div>
							) : (
								// Multi-participant grid view - responsive and modern
								<motion.div
									key="grid-view"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
									className="flex-1 p-3 md:p-6 overflow-y-auto overflow-x-hidden"
								>
									<div
										className="gap-3 md:gap-4"
										style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))'
										}}
									>
										{/* Local participant */}
										{room.localParticipant && (
											<motion.div
												layoutId={room.localParticipant.identity}
												className="relative group aspect-video w-full"
												transition={{
													type: "spring",
													stiffness: 300,
													damping: 30,
												}}
											>
												<div className="h-full w-full bg-gradient-to-br from-muted/20 via-muted/30 to-muted/20 dark:from-muted/30 dark:via-muted/40 dark:to-muted/30 rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-border/30 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10">
													<ParticipantTile
														participant={room.localParticipant}
														isLocal={true}
														onEnlarge={() =>
															handleFocusParticipant(room.localParticipant)
														}
													/>
												</div>
											</motion.div>
										)}

										{participants.map((participant) => (
											<motion.div
												key={participant.identity}
												layoutId={participant.identity}
												className="relative group aspect-video w-full"
												transition={{
													type: "spring",
													stiffness: 300,
													damping: 30,
												}}
											>
												<div className="h-full w-full bg-gradient-to-br from-muted/20 via-muted/30 to-muted/20 dark:from-muted/30 dark:via-muted/40 dark:to-muted/30 rounded-xl md:rounded-2xl overflow-hidden shadow-lg border border-border/30 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10">
													<ParticipantTile
														participant={participant}
														isLocal={false}
														onEnlarge={() =>
															handleFocusParticipant(participant)
														}
													/>
												</div>
											</motion.div>
										))}
									</div>
								</motion.div>
							)}

							{/* Inline Chat Panel */}
							{meetingId && token && (
								<ChatPanel
									meetingId={meetingId}
									token={token}
									participantData={participantData}
									isChatOpen={isChatOpen}
									setIsChatOpen={setIsChatOpen}
									unreadMessageCount={unreadMessageCount}
									setUnreadMessageCount={setUnreadMessageCount}
									messages={messages}
									setMessages={setMessages}
									chatText={chatText}
									setChatText={setChatText}
									isChatLoading={isChatLoading}
									chatError={chatError}
									typingUsers={typingUsers}
									setTypingUsers={setTypingUsers}
								/>
							)}
						</AnimatePresence>
					)}
				</div>

				{/* Control Bar - Hidden in mobile landscape spotlight mode */}
				<div className={cn(
					"flex-shrink-0 px-3 md:px-6 py-3 md:py-4 backdrop-blur-sm shadow-lg transition-all duration-300",
					darkMode
						? "bg-gradient-to-t from-black/70 via-black/60 to-transparent"
						: "bg-gradient-to-t from-white/80 via-white/60 to-transparent",
					isLandscape && focusedParticipant ? "hidden" : "block"
				)}>
					<div className="flex items-center justify-center max-w-[1200px] mx-auto">
						<div className="flex items-center gap-2 md:gap-3">
							<div className="flex items-center gap-1.5 md:gap-2 bg-muted/40 dark:bg-muted/30 border border-border/50 rounded-2xl p-1.5 md:p-2 shadow-sm">
								{/* Microphone Control */}
								{(() => {
									const micState = getMicrophoneButtonState();
									return (
										<Tooltip>
											<TooltipTrigger asChild>
												<div className="relative">
													<Button
														variant={micState.variant}
														size="lg"
														onClick={handleMicrophoneControl}
														className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
													>
														<micState.icon className="h-4 w-4 md:h-5 md:w-5" />
													</Button>
													{micState.showBadge && (
														<Badge
															variant="destructive"
															className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full px-1 flex items-center justify-center text-xs font-bold animate-pulse"
														>
															{micState.badgeContent}
														</Badge>
													)}
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>{micState.label}</p>
											</TooltipContent>
										</Tooltip>
									);
								})()}

								{/* Camera Control */}
								{(() => {
									const cameraState = getCameraButtonState();
									return (
										<Tooltip>
											<TooltipTrigger asChild>
												<div className="relative">
													<Button
														variant={cameraState.variant}
														size="lg"
														onClick={handleCameraControl}
														className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
													>
														<cameraState.icon className="h-4 w-4 md:h-5 md:w-5" />
													</Button>
													{cameraState.showBadge && (
														<Badge
															variant="destructive"
															className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full px-1 flex items-center justify-center text-xs font-bold animate-pulse"
														>
															{cameraState.badgeContent}
														</Badge>
													)}
												</div>
											</TooltipTrigger>
											<TooltipContent>
												<p>{cameraState.label}</p>
											</TooltipContent>
										</Tooltip>
									);
								})()}

								{/* Screen Share */}
								{participantData?.meeting?.is_screen_sharing_enabled && (
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant={isScreenSharing ? "default" : "ghost"}
												size="lg"
												onClick={handleScreenShare}
												disabled={
													!participantData?.meeting?.is_screen_sharing_enabled
												}
												className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
											>
												{isScreenSharing ? (
													<MonitorOff className="h-4 w-4 md:h-5 md:w-5" />
												) : (
													<Monitor className="h-4 w-4 md:h-5 md:w-5" />
												)}
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											<p>
												{isScreenSharing
													? "Stop screen sharing"
													: "Start screen sharing"}
											</p>
										</TooltipContent>
									</Tooltip>
								)}

								{/* Chat Button */}
								{participantData?.meeting?.is_chat_enabled && (
									<Tooltip>
										<TooltipTrigger asChild>
											<div className="relative">
												<Button
													variant={isChatOpen ? "default" : "ghost"}
													size="lg"
													onClick={() => setIsChatOpen(!isChatOpen)}
													disabled={!participantData?.meeting?.is_chat_enabled}
													className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
												>
													<MessageSquare className="h-4 w-4 md:h-5 md:w-5" />
												</Button>
												{unreadMessageCount > 0 && (
													<Badge
														variant="destructive"
														className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full px-1 flex items-center justify-center text-xs font-bold animate-pulse"
													>
														{unreadMessageCount > 99
															? "99+"
															: unreadMessageCount}
													</Badge>
												)}
											</div>
										</TooltipTrigger>
										<TooltipContent>
											<p>Open chat panel</p>
										</TooltipContent>
									</Tooltip>
								)}

								{/* Recording Button */}
								{participantData?.meeting?.is_recording_enabled &&
									participantData?.participant?.is_host && (
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant={isRecording ? "destructive" : "ghost"}
													size="lg"
													onClick={
														isRecording
															? handleStopRecording
															: handleStartRecording
													}
													disabled={
														isStartingRecording ||
														isStoppingRecording ||
														!participantData?.meeting?.is_recording_enabled
													}
													className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
												>
													{isStartingRecording || isStoppingRecording ? (
														<div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-current" />
													) : isRecording ? (
														<CircleStop className="h-4 w-4 md:h-5 md:w-5" />
													) : (
														<Circle className="h-4 w-4 md:h-5 md:w-5" />
													)}
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p>
													{isRecording ? "Stop recording" : "Start recording"}
												</p>
											</TooltipContent>
										</Tooltip>
									)}

								{/* Leave Meeting */}
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="destructive"
											size="lg"
											onClick={() => setIsLeaveSheetOpen(true)}
											className="rounded-xl w-12 h-12 md:w-14 md:h-14 p-0 transition-all duration-200 hover:scale-105 active:scale-95"
										>
											<PhoneOff className="h-4 w-4 md:h-5 md:w-5" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p>Leave meeting</p>
									</TooltipContent>
								</Tooltip>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Meeting Settings Bottom Sheet */}
			<MeetingSettings
				open={isSettingsOpen}
				onClose={() => setIsSettingsOpen(false)}
			/>

			{/* Leave Meeting Bottom Sheet */}
			<LeaveMeetingSheet
				open={isLeaveSheetOpen}
				onClose={() => setIsLeaveSheetOpen(false)}
				isHost={participantData?.participant?.is_host || false}
				onLeave={leaveMeeting}
				onEnd={handleEndMeeting}
				isEndingMeeting={isEndingMeeting}
				meetingType={participantData?.meeting?.type}
			/>
		</TooltipProvider >
	);
};

export default PublicMeetingRoom;
